<template>
  <div :class="['collapsible-container', collapseDirection, collapsed ? 'collapsed' : 'expanded', collapsedBackgroundClass]">
    <div :class="[collapsed ? 'collapsed-content' : 'expanded-content']">
      <slot />
    </div>

    <button type="button" :class="[collapseDirection, 'toggle-btn', collapsed ? 'collapsed' : 'expanded']" @click="toggleCollapse">
      <ChevronDownIcon v-if="collapsed" class="collapsed-arrow-icon h-4 w-4" />
      <ChevronUpIcon v-else class="expanded-arrow-icon h-4 w-4" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/outline'

type CollapsibleDirection = 'left' | 'right' | 'up' | 'down'

defineProps({
  collapsedBackgroundClass: {
    type: String,
    default: 'bg-gray-900',
  },
  collapseDirection: {
    type: String as PropType<CollapsibleDirection>,
    default: 'up',
  },
})

const emit = defineEmits(['collapse'])

const collapsed = ref(false)

function toggleCollapse() {
  collapsed.value = !collapsed.value
  emit('collapse', collapsed.value)
}
</script>

<style lang="stylus" scoped>
.collapsible-container
  @apply relative
  .collapsed-content
    @apply w-0 overflow-hidden
  .expanded-content
    @apply h-full
  .expanded-content:empty + .toggle-btn, .collapsed-content:empty + .toggle-btn
    @apply hidden pointer-events-none
  &.up.collapsed, &.down.collapsed
    @apply h-5 w-full
  .collapsed-content:hover + .toggle-btn, .expanded-content:hover + .toggle-btn
    @apply opacity-70
  .toggle-btn
    @apply  flex absolute z-10 opacity-0 hover_opacity-100 duration-400 transition-opacity appearance-none border-gray-700 bg-gray-800 text-white
    &.collapsed
      @apply opacity-100

    // styles to position the toggle button correctly depending on the collapse direction
    &.left
      right: -1.3125rem
      top: 50%
      transform: translateY(-50%)
      padding: 0.625rem 0.125rem
      @apply border-b border-r border-t rounded-r-lg
      .collapsed-arrow-icon
        transform: rotate(-90deg)
      .expanded-arrow-icon
        transform: rotate(-90deg)
    &.right
      left: -1.3125rem
      top: 50%
      transform: translateY(-50%)
      padding: 0.625rem 0.125rem
      @apply border-b border-l border-t rounded-l-lg
      .collapsed-arrow-icon
        transform: rotate(90deg)
      .expanded-arrow-icon
        transform: rotate(90deg)
    &.down
      left: 50%
      transform: translateX(-50%)
      padding: 0.125rem 0.625rem
      @apply border-b border-l border-r rounded-b-lg top-0
      &.collapsed
        @apply border-t border-l border-r rounded-t-lg rounded-b-none
      .collapsed-arrow-icon
        transform: rotate(180deg)
      .expanded-arrow-icon
        transform: rotate(180deg)
    &.up
      left: 50%
      transform: translateX(-50%)
      padding: 0.125rem 0.625rem
      @apply border-t border-l border-r rounded-t-lg bottom-0
      &.collapsed
        @apply border-b border-l border-r rounded-b-lg rounded-t-none
</style>
